import { Pipe, PipeTransform } from '@angular/core';

/** Maps HTML tag names to the Tailwind classes that should be added. */
const TAG_CLASSES: Record<string, string> = {
  a: 'text-[#444] underline hover:opacity-80 break-words',
  p: 'mb-2 last:mb-0 text-[#444]',
  ul: 'list-disc list-inside mb-2 space-y-1 text-[#444]',
  ol: 'list-decimal list-inside mb-2 space-y-1 text-[#444]',
  li: 'ml-2 text-[#444]',
  strong: 'font-bold text-[#444]',
  b: 'font-bold text-[#444]',
  em: 'italic text-[#444]',
  i: 'italic text-[#444]',
  h1: 'text-lg font-bold mb-1 text-[#444]',
  h2: 'text-base font-bold mb-1 text-[#444]',
  h3: 'text-sm font-bold mb-1 text-[#444]',
  br: '',
};

@Pipe({
  name: 'htmlToTailwind',
  standalone: true,
})
export class HtmlToTailwindPipe implements PipeTransform {
  private applyClasses(node: Element): void {
    for (const child of Array.from(node.children)) {
      const tag = child.tagName.toLowerCase();
      const classes = TAG_CLASSES[tag];
      if (classes) {
        const existing = child.getAttribute('class') ?? '';
        child.setAttribute('class', `${existing} ${classes}`.trim());
      }

      this.applyClasses(child);
    }
  }

  /**
   * Remove dangerous elements and attributes from the parsed DOM.
   * - Drops blacklisted tags (script, style, iframe, object, embed, link).
   * - Removes attributes that begin with `on` (event handlers).
   * - Strips `style`, `srcdoc` and any attributes with `javascript:` or `data:` URIs.
   */
  private sanitizeElement(node: Element): void {
    const blacklist = new Set([
      'script',
      'style',
      'iframe',
      'object',
      'embed',
      'link',
      'meta',
      'base',
    ]);

    for (const child of Array.from(node.children)) {
      const tag = child.tagName.toLowerCase();
      if (blacklist.has(tag)) {
        child.remove();
        continue;
      }

      // Remove dangerous attributes
      const attrs = Array.from(child.attributes).map(a => a.name);
      for (const attr of attrs) {
        const val = child.getAttribute(attr) ?? '';

        // Remove event handlers (on*) and inline styles/srcdoc
        if (
          /^on/i.test(attr) ||
          attr.toLowerCase() === 'style' ||
          attr.toLowerCase() === 'srcdoc'
        ) {
          child.removeAttribute(attr);
          continue;
        }

        // Sanitize href/src-like attributes to avoid javascript: and data: URIs
        if (/^(href|src|xlink:href)$/i.test(attr)) {
          const v = val.trim().toLowerCase();
          if (/^\s*javascript\s*:/i.test(v) || /^\s*data:/i.test(v)) {
            child.removeAttribute(attr);
            continue;
          }
        }
      }

      // Recurse
      this.sanitizeElement(child);
    }
  }

  /**
   * Transform input HTML (or plain-text) into HTML with Tailwind classes applied.
   * Returns a plain string so Angular's built-in sanitizer will run when the
   * result is bound via `[innerHTML]`, providing defense-in-depth.
   */
  transform(html: string | undefined | null): string {
    if (!html) return '';

    // Convert plain-text newlines and tabs to HTML equivalents before parsing
    const normalized = html
      .replaceAll(/\r\n|\r/g, '\n')
      .replaceAll('\t', '&nbsp;&nbsp;&nbsp;&nbsp;')
      .replaceAll(/\n{2,}/g, '</p><p>')
      .replaceAll('\n', '<br>');

    const parser = new DOMParser();
    const doc = parser.parseFromString(normalized, 'text/html');

    this.applyClasses(doc.body);
    this.sanitizeElement(doc.body);

    return doc.body.innerHTML;
  }
}
